import { i18n } from "@/plugins/i18n";
import { required } from "@/_helpers/validators";
import { computed, ref, type Ref } from "vue";
import { useCuiWorkspaceValidation, WorkspaceCreationSteps } from "@coronet/cui";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import { type MspWorkspaceDialogItem, NotificationsLevel } from "@/_store/msp/msp.module.ts";
import { SubscriptionBundle, SubscriptionModule, WorkspaceType } from "@/constants/workplaces";
import { useWorkspaceFormText } from "@/composables/useWorkspaceFormText";

// Re-exported from @coronet/cui so existing `@/composables/useMspWorkspaceModal` imports keep
// working. CreateMspWorkspaceModal builds its own `steps` list, so the enum's member order
// (portal order in cui) does not matter here.
export { WorkspaceCreationSteps };

export function useMspWorkspaceModal(localValue: Ref<DialogDataConfig<MspWorkspaceDialogItem>>) {
  const currentStep = ref(WorkspaceCreationSteps.COMPANY_DETAILS);

  const showPreviousStepBtn = computed(() => {
    return currentStep.value !== WorkspaceCreationSteps.COMPANY_DETAILS;
  });

  const showCompleteBtn = computed(() => {
    return currentStep.value === WorkspaceCreationSteps.REVIEW_SUBSCRIPTION;
  });

  const getCurrentStepNumber = (steps: string[]) => {
    return steps.indexOf(currentStep.value) + 1;
  };

  const getStepsProgress = (steps: string[]) => {
    return (getCurrentStepNumber(steps) * 100) / steps.length;
  };

  const { validationMessages } = useWorkspaceFormText();
  const {
    companyNameRules,
    adminEmailRules,
    supportEmailRules,
    aliasRules,
    brandColorRules,
    displayNameRules,
    domainRules,
  } = useCuiWorkspaceValidation(() => validationMessages.value);

  const bundleLabels = computed((): Record<SubscriptionBundle, string> => {
    return Object.values(SubscriptionBundle).reduce(
      (acc, bundle) => {
        acc[bundle] = i18n.global.t(`subscriptions.bundles.${bundle}`);
        return acc;
      },
      {} as Record<SubscriptionBundle, string>
    );
  });

  const moduleLabels = computed((): Record<SubscriptionModule, string> => {
    return Object.values(SubscriptionModule).reduce(
      (acc, module) => {
        acc[module] = i18n.global.t(`subscriptions.modules.${module}`);
        return acc;
      },
      {} as Record<SubscriptionModule, string>
    );
  });

  const notificationLevels = computed(() => {
    return localValue.value.item.parentWorkspaceId || localValue.value.item.parentDomain
      ? [
          NotificationsLevel.NONE,
          NotificationsLevel.PARENT,
          NotificationsLevel.ALL,
          NotificationsLevel.LOCAL_ADMINS,
        ]
      : [NotificationsLevel.NONE, NotificationsLevel.PARENT, NotificationsLevel.ALL];
  });

  const getNotificationLevelText = (
    workspaceType: WorkspaceType,
    notificationLevel: NotificationsLevel,
    domain?: string
  ) => {
    const isSubchannelWorkspace =
      workspaceType === WorkspaceType.CHANNEL &&
      (localValue.value.item.parentWorkspaceId || localValue.value.item.parentDomain);

    if (isSubchannelWorkspace && notificationLevel === NotificationsLevel.PARENT) {
      return i18n.global.t(
        `modals.createWorkspace.notificationLevels.${workspaceType}.subchannelParent`,
        {
          domain,
        }
      );
    }
    return i18n.global.t(
      `modals.createWorkspace.notificationLevels.${workspaceType}.${notificationLevel}`,
      {
        domain,
      }
    );
  };

  return {
    required,
    bundleLabels,
    moduleLabels,
    companyNameRules,
    adminEmailRules,
    supportEmailRules,
    aliasRules,
    brandColorRules,
    displayNameRules,
    domainRules,
    notificationLevels,
    currentStep,
    showPreviousStepBtn,
    showCompleteBtn,
    getCurrentStepNumber,
    getStepsProgress,
    getNotificationLevelText,
  };
}
